import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import QRCode from "qrcode";
import { zipSync } from "fflate";
import { formatBytes } from "@suite/core";
import { AdBanner, AdConsentLink, HOME_URL, Modal, SocialLinks, useToast } from "@suite/ui";
import { countWord, type DeviceKind } from "./device.ts";
import {
  IDLE_SNAPSHOT,
  ROOM_CODE_LENGTH,
  RoomSession,
  isRoomCode,
  normalizeRoomCode,
  type Download,
  type Peer,
  type RoomSnapshot,
  type SharedFile,
} from "./room.ts";
import { Explainer } from "./Explainer.tsx";

/**
 * No persisted state: the lodge is deliberately ephemeral. Leaving takes your
 * files with you. (The one thing kept is this tab's device id, in sessionStorage
 * — see `deviceIdentity` in room.ts — so a reload rejoins as the same otter
 * rather than as a stranger. It dies with the tab like everything else here.)
 */
export const STORAGE_KEY = "otterdrop:none";

const SIGNAL_URL =
  (import.meta.env["VITE_SIGNAL_URL"] as string | undefined) ??
  (import.meta.env.DEV
    ? `ws://${window.location.hostname}:8787/room`
    : "wss://suite-signal.workers.dev/room");

const INK = "#33291f";

/**
 * An invite code rides in the URL fragment, not the query string: a fragment is
 * never sent to a server, so the one string that grants access to a room stays
 * out of request logs and analytics on the way in.
 */
function codeFromHash(): string | null {
  const match = /^#r=([a-z0-9]+)$/i.exec(window.location.hash);
  const code = match?.[1] ? normalizeRoomCode(match[1]) : null;
  return code && isRoomCode(code) ? code : null;
}

export function App() {
  const { toast } = useToast();
  const [session] = useState(() => new RoomSession(SIGNAL_URL, codeFromHash()));
  const [qr, setQr] = useState<string | null>(null);

  const room = useSyncExternalStore(
    useCallback((listener) => session.subscribe(listener), [session]),
    useCallback(() => session.getSnapshot(), [session]),
    () => IDLE_SNAPSHOT,
  );

  useEffect(() => {
    void session.connect();

    // `pagehide` fires for a real navigation away *and* for a phone merely
    // putting the tab in the back/forward cache — switching apps, locking the
    // screen, moving to another tab. Only the first kind is leaving, and
    // `session.leave()` is final: nothing in the page ever reconnects after it.
    // Treating the second kind as a departure is what made a tab switch look
    // like a disconnect there was no way back from.
    const onHide = (event: PageTransitionEvent) => {
      if (!event.persisted) session.leave();
    };
    // The return trip. A page can come back from the back/forward cache, or be
    // un-frozen in place, and both arrive here; `resume` is cheap and idempotent
    // when nothing was actually lost.
    const onShow = () => session.resume();
    const onVisible = () => {
      if (document.visibilityState === "visible") session.resume();
    };

    window.addEventListener("pagehide", onHide);
    window.addEventListener("pageshow", onShow);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("pageshow", onShow);
      document.removeEventListener("visibilitychange", onVisible);
      session.leave();
    };
  }, [session]);

  // The room decides the code (this device may have been pulled into one by a
  // peer); the URL and the QR follow it, so whatever is on screen is joinable.
  useEffect(() => {
    const want = room.code ? `#r=${room.code}` : "";
    const href = `${window.location.pathname}${window.location.search}${want}`;
    if (window.location.hash !== want) {
      window.history.replaceState(null, "", href);
    }
    QRCode.toDataURL(window.location.href, {
      margin: 0,
      width: 176,
      errorCorrectionLevel: "M",
      color: { dark: INK, light: "#00000000" },
    })
      .then(setQr)
      .catch(() => setQr(null));
  }, [room.code]);

  const saved = useRef(new Set<string>());
  const [batch, setBatch] = useState<Set<string> | null>(null);
  useEffect(() => {
    for (const download of Object.values(room.downloads)) {
      if (!download.done || !download.blobUrl || saved.current.has(download.fileId)) continue;
      if (batch?.has(download.fileId)) continue;
      saved.current.add(download.fileId);
      const file = room.files.find((f) => f.id === download.fileId);
      saveBlob(download.blobUrl, file?.name ?? "file");
    }
  }, [room.downloads, room.files, batch]);

  useEffect(() => {
    if (!batch) return;
    const entries = [...batch].map((id) => room.downloads[id]);
    if (entries.some((d) => !d || (!d.done && !d.failed))) return;
    setBatch(null);
    for (const id of batch) saved.current.add(id);
    const arrived = entries.filter((d): d is Download => Boolean(d?.done && d.blob));
    const missed = entries.length - arrived.length;
    if (missed > 0) {
      toast(`${missed} ${missed === 1 ? "file" : "files"} couldn't be fetched — see the raft.`, "warning");
    }
    if (arrived.length === 0) return;
    void zipAndSave(arrived, room.files, "otterdrop.zip").catch(() =>
      toast("Couldn't build the zip. Save the files one by one instead.", "warning"),
    );
  }, [batch, room.downloads, room.files, toast]);

  const deviceCount = room.peers.length + 1;

  const downloadAll = () => {
    const requested = session.downloadAll();
    if (requested.length === 0) toast("Nothing new on the raft.", "info");
    else if (requested.length > 1) {
      setBatch(new Set(requested));
      toast(`Fetching ${requested.length} files — they'll be saved together as one zip.`, "info");
    }
  };

  const available = room.files.filter((file) => {
    if (file.mine) return false;
    const download = room.downloads[file.id];
    return !download || (download.failed !== null && !download.done);
  }).length;

  return (
    <div className="grain relative min-h-dvh overflow-hidden bg-cream font-sans text-ink">
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[1080px] flex-col px-5 pb-8 pt-0 sm:px-8 lg:px-10">
        {/* Header */}
        <header className="flex items-stretch justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-baseline gap-3.5">
            <h1 className="flex shrink-0 items-center gap-2.5 pt-6 font-serif text-[30px] font-semibold leading-none text-pine sm:text-[34px]">
              <img src="/logo.png" alt="" width="34" height="34" className="size-[30px] sm:size-[34px]" />
              OtterDrop
              <span className="sr-only">: send files between phone and computer over Wi-Fi</span>
            </h1>
            <div className="flex flex-1 items-end self-stretch border-b border-rule pb-2.5">
              <p className="hidden text-[13px] text-muted sm:block">
                share files with the devices around you
              </p>
            </div>
          </div>
          <StatusBar room={room} deviceCount={deviceCount} />
        </header>

        {/* Headline + mascot */}
        <section className="relative mt-9 lg:mt-14">
          <img
            src="/otter/otter-mascot.png"
            alt=""
            aria-hidden="true"
            className="bob pointer-events-none absolute z-0 right-2 top-0 hidden h-[128px] w-auto drop-shadow-[0_10px_12px_rgba(46,30,16,0.2)] sm:block lg:right-8 lg:-top-1 lg:h-[150px]"
          />
          <h2 className="relative z-10 max-w-[820px] font-serif text-[26px] font-semibold leading-[1.1] tracking-[-0.01em] text-pine min-[400px]:text-[30px] sm:text-[56px] sm:leading-[1.0] lg:text-[64px] lg:leading-[0.98]">
            <Headline status={room.status} deviceCount={deviceCount} />
          </h2>
        </section>

        {/* Main */}
        <div className="mt-10 grid items-start gap-8 lg:mt-14 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-x-14 lg:gap-y-8">
          <div className="order-1 lg:col-start-1 lg:row-start-1">
            <AddDeviceCard qr={qr} code={room.code} onJoin={(code) => session.moveTo(code)} />
          </div>

          <div className="order-2 min-w-0 lg:col-start-2 lg:row-start-2">
            <Ledger
              room={room}
              available={available}
              onUnshare={(id) => session.unshare(id)}
              onDownload={(id) => {
                saved.current.delete(id);
                session.download(id);
              }}
              onDownloadAll={downloadAll}
            />
          </div>

          <div className="order-3 min-w-0 lg:col-start-2 lg:row-start-1 lg:self-stretch">
            <PondDrop onFiles={(files) => session.share(files)} />
          </div>

          <div className="order-4 lg:col-start-1 lg:row-start-2">
            <RoomCard
              selfName={room.selfName}
              selfLabel={room.selfLabel}
              selfKind={room.selfKind}
              peers={room.peers}
              status={room.status}
              detail={room.detail}
            />
          </div>
        </div>

        {/* Ad — directly under the tool. Renders nothing until a client id, slot and consent all exist. */}
        <AdBanner
          format="responsive"
          slot={import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined}
          className="mx-auto mt-16 max-w-[728px]"
        />

        {/* What this is, how it works, FAQ — static content, also prerendered for crawlers. */}
        <Explainer />

        {/* Footer */}
        <footer className="mt-16 flex flex-col gap-6 border-t border-rule pt-6">
          <ul className="flex min-w-0 flex-wrap items-end gap-x-8 gap-y-4">
            <Fact n="01" text="Peer-to-peer" img="/otter/otter-p2p.png" />
            <Fact n="02" text="Encrypted end to end" img="/otter/otter-lock.png" />
            <Fact n="03" text="Nothing stored, ever" img="/otter/otter-stream.png" />
          </ul>
          <nav aria-label="Site" className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-t border-rule/60 pt-4">
            <a
              href={HOME_URL}
              className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-rule bg-surface pl-2 pr-3 text-[13px] font-medium text-soft transition-colors hover:border-pine hover:text-pine">
              <img src="/logo.png" alt="" width="18" height="18" className="size-[18px]" />
              More tools
            </a>
            <SocialLinks className="mr-auto" />
            <p className="flex gap-4 whitespace-nowrap text-[12px] text-muted">
              <a href={`${HOME_URL}/about`} className="link">About</a>
              <a href={`${HOME_URL}/contact`} className="link">Contact</a>
              <a href="/privacy" className="link">Privacy</a>
              <a href="/terms" className="link">Terms</a>
              <AdConsentLink />
            </p>
          </nav>
        </footer>
      </div>
    </div>
  );
}

// ---- header pieces --------------------------------------------------------

function StatusBar({ room, deviceCount }: { room: RoomSnapshot; deviceCount: number }) {
  const dot =
    room.status === "open"
      ? "bg-[#8fd17a]"
      : room.status === "failed"
        ? "bg-danger"
        : "bg-cream/70 breathe";
  const label =
    room.status === "open"
      ? `${deviceCount} ${deviceCount === 1 ? "device" : "devices"} here`
      : room.status === "failed"
        ? "not connected"
        : room.status === "closed"
          ? "disconnected"
          : "paddling in…";
  return (
    <div className="-mr-5 flex h-[46px] items-center gap-3 rounded-bl-[20px] bg-teal pl-4 pr-6 text-cream shadow-[0_6px_14px_-8px_rgba(31,63,57,0.6)] sm:-mr-8 lg:-mr-10">
      <span className={`inline-block size-2 rounded-full ${dot}`} aria-hidden="true" />
      <span className="font-mono text-[12px] uppercase tracking-[0.14em]">{label}</span>
    </div>
  );
}

function Headline({ status, deviceCount }: { status: RoomSnapshot["status"]; deviceCount: number }) {
  if (status === "connecting" && deviceCount === 1) {
    return (
      <>
        Paddling into the lodge, <em className="ripple">one moment.</em>
      </>
    );
  }
  if (deviceCount === 1) {
    return (
      <>
        You&rsquo;re <em className="ripple">floating in the lodge,</em>
        <br />
        <em className="ripple">waiting for the raft to join.</em>
      </>
    );
  }
  return (
    <>
      {countWord(deviceCount)} otters, <em className="ripple">one lodge.</em>
    </>
  );
}

// ---- left column ----------------------------------------------------------

/**
 * Every room has a code from the moment you land, so this card has one state
 * rather than two: the code, the link, and the QR that carries it. That is what
 * makes the QR worth scanning from a phone on mobile data — before, it opened a
 * page that quietly put you in a different room.
 *
 * Nobody is asked for a code on the way in; the two buttons are for the
 * uncommon cases — typing one somebody read to you, and burning the current one.
 */
function AddDeviceCard({
  qr,
  code,
  onJoin,
}: {
  qr: string | null;
  code: string | null;
  onJoin: (code: string) => void;
}) {
  const { toast } = useToast();
  const [joining, setJoining] = useState(false);

  const copy = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => toast("Link copied"))
      .catch(() => toast("Could not copy — select the link instead"));
  };

  return (
    <div className="flex flex-col gap-4 rounded-[14px] border border-rule bg-surface p-5 shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center gap-4">
        <div className="shrink-0 rounded-[8px] border border-rule bg-[#fbfaf5] p-1.5">
          {qr ? (
            <img
              src={qr}
              alt="QR code that opens this room"
              className="size-[76px]"
              width={76}
              height={76}
            />
          ) : (
            <div className="size-[76px]" />
          )}
        </div>
        <div className="flex min-w-0 flex-col gap-1">
          <span className="eyebrow">Room code</span>
          {code ? (
            <p className="font-mono text-[30px] font-semibold uppercase leading-none tracking-[0.18em] text-pine">
              {code}
            </p>
          ) : (
            // Before the room answers there is nothing honest to show, and a
            // placeholder code would be read out and typed in.
            <p className="font-serif text-[15px] italic leading-none text-muted">
              paddling in&hellip;
            </p>
          )}
          <p className="text-[12px] leading-[1.45] text-soft">
            Scan from the other devices or use the link below.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 border-t border-rule pt-3.5">
        <button
          type="button"
          onClick={copy}
          disabled={!code}
          className="flex min-w-0 items-center gap-2 rounded-[8px] border border-rule bg-[#fbfaf5] px-3 py-2 text-left transition-colors hover:border-pine disabled:opacity-50"
        >
          <span className="min-w-0 flex-1 truncate font-mono text-[11px] text-ink">
            {code ? `${window.location.host}/#r=${code}` : window.location.host}
          </span>
          <span className="shrink-0 text-[11px] font-medium text-pine">Copy</span>
        </button>
        <button
          type="button"
          onClick={() => setJoining(true)}
          className="flex h-9 w-full items-center justify-center gap-2 rounded-full border border-rule bg-cream-2/50 px-4 text-[12.5px] font-medium text-pine transition-colors hover:border-pine hover:bg-cream-2 hover:text-pine-deep"
        >
          <EnterIcon />
          Enter a different room
        </button>
      </div>

      <JoinDialog
        open={joining}
        onOpenChange={setJoining}
        onJoin={(entered) => {
          setJoining(false);
          onJoin(entered);
        }}
      />
    </div>
  );
}

/**
 * Typing in a code somebody read to you. Kept as a dialog rather than a field
 * on the page: almost nobody needs it, and a permanent input would suggest the
 * room is something you have to sign in to.
 */
function JoinDialog({
  open,
  onOpenChange,
  onJoin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onJoin: (code: string) => void;
}) {
  const [value, setValue] = useState("");
  const inputId = useId();
  const code = normalizeRoomCode(value);
  const ready = isRoomCode(code);

  // A stale code left in the box is confusing the next time it opens.
  useEffect(() => {
    if (open) setValue("");
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Join with a code"
      description="Four characters, from the other device's screen."
      className="rounded-[14px] border-rule"
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (ready) onJoin(code);
        }}
        className="flex flex-col gap-4"
      >
        <label htmlFor={inputId} className="sr-only">
          Room code
        </label>
        <input
          id={inputId}
          value={value}
          onChange={(event) => setValue(event.target.value.slice(0, ROOM_CODE_LENGTH + 2))}
          autoFocus
          autoComplete="off"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="text"
          placeholder="A1B2"
          aria-describedby={`${inputId}-hint`}
          className="w-full rounded-[10px] border border-rule bg-[#fbfaf5] px-4 py-3 text-center font-mono text-[30px] font-semibold uppercase tracking-[0.3em] text-pine outline-none placeholder:text-muted/50 focus:border-pine"
        />
        <p id={`${inputId}-hint`} className="text-[12px] leading-[1.45] text-muted">
          The other device shows its code under <em>Room code</em>. Letters and digits, and
          it doesn&rsquo;t matter whether you type capitals.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-full border border-rule px-4 py-2 text-[13px] font-medium text-soft transition-colors hover:border-pine hover:text-pine"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!ready}
            className="rounded-full bg-pine px-4 py-2 text-[13px] font-medium text-cream transition-opacity disabled:opacity-40"
          >
            Join
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RoomCard({
  selfName,
  selfLabel,
  selfKind,
  peers,
  status,
  detail,
}: {
  selfName: string;
  selfLabel: string;
  selfKind: DeviceKind;
  peers: Peer[];
  status: RoomSnapshot["status"];
  detail: string | null;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-5 py-3">
        <span className="eyebrow">In the room</span>
        <span className="min-w-[22px] rounded-full bg-pine/10 px-2 py-0.5 text-center font-mono text-[11px] font-semibold text-pine">
          {peers.length + 1}
        </span>
      </div>

      <div className="flex flex-col gap-4 p-5">
        <PeerRow name={selfName} label={selfLabel} kind={selfKind} you />
        {peers.map((peer) => (
          <PeerRow
            key={peer.id}
            name={peer.name}
            label={peer.label}
            kind={peer.kind}
            state={peer.state}
            fileCount={peer.fileCount}
          />
        ))}

        {status === "connecting" ? (
          <p className="font-serif text-[14px] italic text-muted">
            {detail ?? "paddling into the lodge…"}
          </p>
        ) : null}
        {status === "failed" ? (
          <p className="text-[12px] leading-[1.5] text-danger">
            {detail}{" "}
            <button type="button" onClick={() => location.reload()} className="link font-semibold">
              try again
            </button>
          </p>
        ) : null}
      </div>
    </div>
  );
}

function PeerRow({
  name,
  label,
  kind,
  you = false,
  state = "connected",
  fileCount = 0,
}: {
  name: string;
  label: string;
  kind: DeviceKind;
  you?: boolean;
  state?: Peer["state"];
  fileCount?: number;
}) {
  const meta = you
    ? "You"
    : state === "failed"
      ? "adrift"
      : state === "connecting"
        ? "paddling…"
        : `${fileCount} ${fileCount === 1 ? "file" : "files"}`;
  const metaTone = you ? "text-pine" : state === "failed" ? "text-danger" : "text-pine-deep/60";
  return (
    <div className="flex items-center gap-2.5">
      <span
        className={`flex size-8 shrink-0 items-center justify-center rounded-[7px] border ${
          you ? "border-pine/40 bg-pine/10 text-pine" : "border-rule bg-cream-2 text-soft"
        }`}
      >
        <DeviceIcon kind={kind} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-serif text-[17px] leading-tight text-pine-deep">{name}</span>
        {label ? (
          <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-pine-deep/55">
            {label}
          </span>
        ) : null}
      </span>
      <span className={`shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] ${metaTone}`}>{meta}</span>
    </div>
  );
}

// ---- right column ---------------------------------------------------------

function PondDrop({ onFiles }: { onFiles: (files: File[]) => void }) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const inputId = useId();

  const accept = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
  };

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        accept(e.dataTransfer.files);
      }}
      className={`h-full rounded-[18px] border-2 border-dashed transition-colors ${
        dragging
          ? "border-teal bg-teal/12"
          : "border-teal/45 bg-teal/[0.06]"
      }`}
    >
      <label
        htmlFor={inputId}
        className="flex h-full min-h-[176px] cursor-pointer flex-col items-center justify-center gap-2 px-6 py-12 text-center"
      >
        <span className="font-serif text-[21px] font-semibold leading-tight text-pine sm:text-[30px]">
          Drop files here to share them
        </span>
        <span className="text-[13px] text-soft">
          or <span className="link font-medium text-teal-deep">choose from this device</span>
        </span>
        <span className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
          They stay on your device until someone takes them
        </span>
      </label>
      <input
        id={inputId}
        type="file"
        multiple
        className="sr-only"
        onChange={(e) => {
          accept(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

const COLS = "grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[minmax(0,1fr)_128px_88px_128px]";

function Ledger({
  room,
  available,
  onUnshare,
  onDownload,
  onDownloadAll,
}: {
  room: RoomSnapshot;
  available: number;
  onUnshare: (id: string) => void;
  onDownload: (id: string) => void;
  onDownloadAll: () => void;
}) {
  // Nothing to list: no column headings over an empty table, and no Download
  // all for a raft with nothing on it. Just the note, sitting on the page
  // rather than in a panel of its own.
  if (room.files.length === 0) {
    return (
      <div className="flex items-start gap-3.5 rounded-[14px] border border-dashed border-rule/70 px-4 py-6 sm:px-5">
        <span className="mt-px flex size-8 shrink-0 items-center justify-center rounded-[7px] border border-rule/70 text-muted">
          <WavesIcon />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          <p className="font-serif text-[17px] font-semibold leading-tight text-pine/85">
            Nothing on the raft yet
          </p>
          <p className="text-[12.5px] leading-[1.5] text-muted">
            Files you drop show up here, and so does anything the other devices share.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className={`${COLS} mb-2 items-end gap-x-3 border-b border-rule pb-3`}>
        <span className="eyebrow">File</span>
        <span className="eyebrow hidden sm:block">From</span>
        <span className="eyebrow hidden sm:block">Size</span>
        <button
          type="button"
          onClick={onDownloadAll}
          disabled={available === 0}
          className="flex h-9 items-center gap-2 justify-self-end rounded-full border-2 border-teal-deep bg-teal px-3.5 text-[12px] font-semibold text-cream shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_5px_-2px_rgba(31,63,57,0.6)] transition-opacity hover:bg-teal-deep disabled:opacity-40"
        >
          <DownIcon />
          Download all
        </button>
      </div>

      <ul className="scroll-thin max-h-[420px] overflow-y-auto overscroll-contain pr-1">
        {room.files.map((file) => (
          <Row
            key={file.id}
            file={file}
            download={room.downloads[file.id]}
            takenBy={room.takenBy[file.id] ?? 0}
            sendingTo={room.sendingTo[file.id] ?? 0}
            onUnshare={onUnshare}
            onDownload={onDownload}
          />
        ))}
      </ul>
    </div>
  );
}

function Row({
  file,
  download,
  takenBy,
  sendingTo,
  onUnshare,
  onDownload,
}: {
  file: SharedFile;
  download: Download | undefined;
  takenBy: number;
  sendingTo: number;
  onUnshare: (id: string) => void;
  onDownload: (id: string) => void;
}) {
  const receiving = download && !download.done && !download.failed;
  const pct = download && download.size > 0 ? Math.round((download.received / download.size) * 100) : 0;

  return (
    <li className={`${COLS} min-h-[60px] items-center gap-x-3 border-b border-rule`}>
      <div className="relative flex min-w-0 items-center gap-2.5 self-stretch py-3.5">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-[7px] border border-teal/40 bg-teal/12 text-teal-deep">
          <FileIcon name={file.name} />
        </span>
        <span
          className={`truncate text-[14px] font-medium ${download?.done ? "text-muted" : "text-ink"}`}
          title={file.name}
        >
          {file.name}
        </span>
        <span className="ml-auto shrink-0 font-mono text-[11px] text-muted sm:hidden">
          {formatBytes(file.size)}
        </span>
        {receiving ? (
          <span
            className="absolute -bottom-px left-0 h-[2px] bg-teal transition-[width]"
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-valuenow={pct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Receiving ${file.name}`}
          />
        ) : null}
      </div>
      <span className="hidden min-w-0 justify-self-start sm:block">
        <span
          title={file.ownerName}
          className={`inline-flex max-w-full items-center truncate whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold ${
            file.mine ? "bg-pine/10 text-pine" : "bg-cream-2 text-soft"
          }`}
        >
          {file.ownerName}
        </span>
      </span>
      <span className="hidden justify-self-start font-mono text-[11px] text-muted sm:block">
        {formatBytes(file.size)}
      </span>
      <div className="flex justify-end text-right">
        {file.mine ? (
          <span className="text-[11px] text-muted">
            {sendingTo > 0 ? `sending to ${sendingTo}` : takenBy > 0 ? `taken by ${takenBy}` : "not taken yet"}{" "}
            ·{" "}
            <button type="button" onClick={() => onUnshare(file.id)} className="link">
              remove
            </button>
          </span>
        ) : download?.failed ? (
          <span className="font-mono text-[11px] text-danger" title={download.failed}>
            failed ·{" "}
            <button type="button" onClick={() => onDownload(file.id)} className="link">
              retry
            </button>
          </span>
        ) : receiving ? (
          <span className="font-mono text-[11px] text-teal-deep">receiving · {pct}%</span>
        ) : download?.done ? (
          <span className="flex items-center gap-1.5 text-[11px] text-ok">
            <CheckIcon /> saved ·{" "}
            <a href={download.blobUrl ?? undefined} download={file.name} className="link text-muted">
              again
            </a>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => onDownload(file.id)}
            className="link text-[12px] font-semibold text-pine"
          >
            Download ↓
          </button>
        )}
      </div>
    </li>
  );
}

// ---- footer ---------------------------------------------------------------

function Fact({ n, text, img }: { n: string; text: string; img: string }) {
  return (
    <li className="flex flex-col items-start gap-1.5">
      <img src={img} alt="" aria-hidden="true" className="h-9 w-auto" />
      <span className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] text-pine">{n}</span>
        <span className="text-[13px] text-ink">{text}</span>
      </span>
    </li>
  );
}

// ---- icons ----------------------------------------------------------------

function DeviceIcon({ kind }: { kind: DeviceKind }) {
  if (kind === "phone") {
    return (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="7" y="3" width="10" height="18" rx="2.5" /><path d="M11 18h2" />
      </svg>
    );
  }
  if (kind === "tablet") {
    return (
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="4" y="3" width="16" height="18" rx="2.5" /><path d="M11 18h2" />
      </svg>
    );
  }
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="4" width="18" height="12" rx="2" /><path d="M2 20h20" />
    </svg>
  );
}

function FileIcon({ name }: { name: string }) {
  const image = /\.(png|jpe?g|gif|webp|avif|heic|svg)$/i.test(name);
  if (image) {
    return (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="m21 15-5-4-8 7" />
      </svg>
    );
  }
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" />
    </svg>
  );
}

function EnterIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" /><path d="m10 17 5-5-5-5" /><path d="M15 12H3" />
    </svg>
  );
}

function WavesIcon() {
  return (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 7q2.5-3 5 0t5 0t5 0t5 0" /><path d="M2 12q2.5-3 5 0t5 0t5 0t5 0" /><path d="M2 17q2.5-3 5 0t5 0t5 0t5 0" />
    </svg>
  );
}

function DownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v12" /><path d="m6 12 6 6 6-6" /><path d="M4 20h16" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}


// ---- helpers --------------------------------------------------------------

async function zipAndSave(downloads: Download[], files: SharedFile[], zipName: string): Promise<void> {
  const entries: Record<string, [Uint8Array, { level: 0 }]> = {};
  for (const download of downloads) {
    const file = files.find((f) => f.id === download.fileId);
    let name = file?.name ?? download.fileId;
    if (name in entries) name = `${file?.ownerName ?? "copy"} — ${name}`;
    const bytes = new Uint8Array(await (download.blob as Blob).arrayBuffer());
    entries[name] = [bytes, { level: 0 }];
  }
  const zipped = zipSync(entries);
  const url = URL.createObjectURL(new Blob([zipped as BlobPart], { type: "application/zip" }));
  saveBlob(url, zipName);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function saveBlob(url: string, name: string): void {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = name;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}
